import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, EffectFade } from "swiper/modules";
import { ArrowLeft, ArrowRight, Send } from "react-feather";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-fade";

const slides = [
  {
    id: "website-development",
    tag: "Our Services",
    title: "Website Development",
    desc: "CBS provides expert website development, creating, designing, and maintaining functional, high-performance digital spaces.",
    points: [
      "Static Websites",
      "Dynamic Websites",
      "CMS Solutions",
      "Responsive Web Apps",
    ],
    img: "/services/webdev.jpg",
  },
  {
    id: "application-development",
    tag: "Software Solutions",
    title: "Application Development",
    desc: "CBS delivers custom application development services for web, mobile, desktop, and cross-platform solutions built for performance, scalability, and long-term growth.",
    points: [
      "Web Applications",
      "Mobile Apps (iOS & Android)",
      "Desktop Software",
      "Hybrid Cross-Platform Apps",
    ],
    img: "/services/appdev.jpg",
  },
  {
    id: "ecommerce-solutions",
    tag: "E-commerce",
    title: "E-commerce Solutions",
    desc: "Scale your business with our robust B2B and B2C platforms designed for seamless user experiences and high conversion.",
    points: [
      "B2C Marketplaces",
      "B2B Trade Portals",
      "Secure Payment Integration",
      "Inventory Management",
    ],
    img: "/services/ecomservice.jpg",
  },
  {
    id: "ai-integration",
    tag: "AI & Innovation",
    title: "AI Integration Services",
    desc: "Leverage pre-trained models and fine-tuning to embed intelligence into your business processes, from chatbots to predictive analytics.",
    points: [
      "Workflow Automation",
      "NLP & Chatbots",
      "Computer Vision",
      "Model Fine-Tuning",
    ],
    img: "/services/aiservices.jpg",
  },
  {
    id: "cloud-managed",
    tag: "Infrastructure",
    title: "Cloud & Managed Services",
    desc: "Complete management of cloud resources including migration, security, and optimization powered by AWS Certified Engineers.",
    points: [
      "Cloud Migration",
      "System Upgrades",
      "Data Center Virtualization",
      "24/7 Maintenance",
    ],
    img: "/services/cloud.jpg",
  },
];

export default function Swipertwo() {
  const swiperRef = useRef();

  return (
    <div className="relative group/swiper">
      <Swiper
        loop={true}
        effect={"fade"}
        fadeEffect={{ crossFade: true }}
        modules={[Navigation, Autoplay, EffectFade]}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        onBeforeInit={(swiper) => (swiperRef.current = swiper)}
        className="text-white">
        {slides.map((slide, idx) => (
          <SwiperSlide key={idx}>
            <div className="lg:flex items-center gap-10 py-6">
              {/* LEFT CONTENT */}
              <div className="lg:w-1/2 text-start mt-8 lg:mt-0">
                <motion.div>
                  <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-[#007BFF] to-[#05DFAD] text-white uppercase tracking-[0.2em] mb-6">
                    {slide.tag}
                  </span>

                  <h3 className="text-3xl md:text-4xl font-bold mb-6 text-white leading-tight">
                    {slide.title}
                  </h3>

                  <p className="text-slate-400 text-base md:text-lg leading-relaxed max-w-xl">
                    {slide.desc}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
                    {slide.points.map((point, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 text-slate-300 text-sm bg-white/5 px-4 py-3 rounded-xl">
                        <Send size={15} className="text-[#05DFAD]" />
                        {point}
                      </div>
                    ))}
                  </div>

                  <Link to={`/services/${slide.id}`}>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="mt-8 px-8 py-3 bg-gradient-to-r from-[#007BFF] to-[#05DFAD] rounded-xl text-white font-bold uppercase tracking-widest text-sm hover:opacity-90 transition-opacity shadow-lg">
                      Learn More
                    </motion.button>
                  </Link>
                </motion.div>
              </div>

              {/* RIGHT IMAGE */}
              <div className="lg:w-1/2">
                <motion.div className="relative group">
                  <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-emerald-400 rounded-3xl blur-xl opacity-10 group-hover:opacity-20 transition" />

                  <div className="relative bg-white/5 border border-white/10 backdrop-blur-xl rounded-[28px] p-5 shadow-2xl">
                    <img
                      className="max-w-full rounded-3xl max-h-full w-auto h-auto object-contain drop-shadow-2xl transition-transform duration-500 group-hover:scale-105"
                      src={slide.img}
                      alt={slide.title}
                    />
                  </div>
                </motion.div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* CONTROLS */}
      <div className="flex justify-start gap-3 mt-8">
        <button
          onClick={() => swiperRef.current?.slidePrev()}
          className="w-12 h-12 rounded-full border border-slate-700 flex items-center justify-center text-white hover:border-[#007BFF] hover:bg-slate-800 transition-all active:scale-90">
          <ArrowLeft size={20} />
        </button>

        <button
          onClick={() => swiperRef.current?.slideNext()}
          className="w-12 h-12 rounded-full bg-gradient-to-r from-[#007BFF] to-[#05DFAD] flex items-center justify-center text-white shadow-lg hover:shadow-blue-500/20 transition-all active:scale-90">
          <ArrowRight size={20} />
        </button>
      </div>
    </div>
  );
}
